/**
 * Users table with row action menu.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { MoreVertical, Briefcase, HardDrive } from 'lucide-react'
import type { User } from '../../types'
import { formatDataVolume, getRoleBadgeColor, getStatusBadgeColor } from './helpers'
import { UserActionMenu } from './UserActionMenu'

interface UsersTableProps {
  users: User[]
  currentUserId: string | undefined
  isPlatformAdmin: boolean
  actionMenuUser: string | null
  setActionMenuUser: (id: string | null) => void
  getOrgName: (orgId: string | null) => string
  onEdit: (user: User) => void
  onToggleStatus: (user: User) => void
  onResetPassword: (user: User) => void
  onDelete: (user: User) => void
}

export function UsersTable(props: UsersTableProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const { users } = props

  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <table className="w-full">
        <UsersTableHead />
        <tbody className="divide-y divide-stone-200 dark:divide-stone-700">
          {users.map((user) => (
            <UserRow key={user.id} user={user} {...props} />
          ))}
        </tbody>
      </table>
      {users.length === 0 && (
        <div className="p-8 text-center text-stone-500 dark:text-stone-400">
          {t('users.noUsersFound')}
        </div>
      )}
    </div>
  )
}

function UsersTableHead(): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <thead className="bg-stone-50 dark:bg-stone-900">
      <tr>
        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('users.table.user')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('users.table.organisation')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('users.table.role')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('users.table.status')}
        </th>
        <th className="px-4 py-3 text-center text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          <div className="flex items-center justify-center gap-1">
            <Briefcase className="h-3.5 w-3.5" />
            {t('users.table.cases')}
          </div>
        </th>
        <th className="px-4 py-3 text-center text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          <div className="flex items-center justify-center gap-1">
            <HardDrive className="h-3.5 w-3.5" />
            {t('users.table.data')}
          </div>
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('users.table.lastLogin')}
        </th>
        <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-stone-500 uppercase dark:text-stone-400">
          {t('labels.actions', { ns: 'common' })}
        </th>
      </tr>
    </thead>
  )
}

interface UserRowProps extends UsersTableProps {
  user: User
}

function UserIdentityCell({ user }: { user: User }): React.ReactElement {
  return (
    <td className="px-4 py-4">
      <div>
        <div className="font-medium text-stone-900 dark:text-stone-100">
          {user.displayName ?? user.username}
        </div>
        <div className="text-sm text-stone-500 dark:text-stone-400">{user.email}</div>
      </div>
    </td>
  )
}

function UserRow({
  user,
  currentUserId,
  isPlatformAdmin,
  actionMenuUser,
  setActionMenuUser,
  getOrgName,
  onEdit,
  onToggleStatus,
  onResetPassword,
  onDelete,
}: UserRowProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const isMenuOpen = actionMenuUser === user.id

  return (
    <tr className="hover:bg-stone-50 dark:hover:bg-stone-900/50">
      <UserIdentityCell user={user} />
      <td className="px-4 py-4">
        <span className="text-sm text-stone-700 dark:text-stone-300">
          {getOrgName(user.organisationId ?? null)}
        </span>
      </td>
      <td className="px-4 py-4">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getRoleBadgeColor(user.role)}`}
        >
          {user.role.replace('_', ' ')}
        </span>
      </td>
      <td className="px-4 py-4">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeColor(user.status)}`}
        >
          {user.status}
        </span>
      </td>
      <td className="px-4 py-4 text-center">
        <span className="text-sm font-medium text-stone-600 dark:text-stone-400">
          {user.caseCount ?? '—'}
        </span>
      </td>
      <td className="px-4 py-4 text-center">
        <span className="text-sm font-medium text-stone-600 dark:text-stone-400">
          {user.dataVolumeBytes !== undefined ? formatDataVolume(user.dataVolumeBytes) : '—'}
        </span>
      </td>
      <td className="px-4 py-4 text-sm text-stone-500 dark:text-stone-400">
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : t('users.never')}
      </td>
      <td className="relative px-4 py-4 text-right">
        <button
          onClick={() => {
            setActionMenuUser(isMenuOpen ? null : user.id)
          }}
          className="rounded p-1 hover:bg-stone-100 dark:hover:bg-stone-700"
          disabled={user.id === currentUserId}
        >
          <MoreVertical className="h-5 w-5 text-stone-500" />
        </button>
        {isMenuOpen && (
          <UserActionMenu
            user={user}
            isPlatformAdmin={isPlatformAdmin}
            onEdit={onEdit}
            onToggleStatus={onToggleStatus}
            onResetPassword={onResetPassword}
            onDelete={onDelete}
          />
        )}
      </td>
    </tr>
  )
}
